// src/components/BlobsField.jsx
import React, { useEffect, useRef } from "react";

// Muted translucent palette (matches your original)
const COLORS = [
  "rgba(41,233,182,0.15)",
  "rgba(249,156,248,0.14)",
  "rgba(255,249,169,0.13)",
  "rgba(151,204,255,0.15)",
  "rgba(186,255,190,0.14)",
  "rgba(135,189,255,0.13)",
  "rgba(161,182,255,0.15)",
  "rgba(255,194,244,0.13)",
];

const DEFAULT_COUNT = 44;

function rand(min, max) {
  return Math.random() * (max - min) + min;
}

function createCircle(w, h) {
  const r = rand(18, 40);            // radius in CSS pixels
  const speed = rand(0.4, 1.0);
  const angle = rand(Math.PI, Math.PI * 1.45); // drift up-left
  // start somewhere on/just outside the view
  const spawnEdge = Math.random();
  let x, y;
  if (spawnEdge < 0.33) { x = rand(w * 1.02, w * 1.18); y = rand(-h * 0.2, h * 1.18); }
  else if (spawnEdge < 0.66) { x = rand(-w * 0.1, w * 1.14); y = rand(h * 1.03, h * 1.19); }
  else { x = rand(w * 1.06, w * 1.22); y = rand(h * 1.09, h * 1.27); }

  return {
    x, y, r,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    dx: -Math.abs(Math.cos(angle) * speed),
    dy: -Math.abs(Math.sin(angle) * speed),
  };
}

export default function BlobsField({
  className = "login__blobfield",
  count = DEFAULT_COUNT,
  style = {},
}, ref) {
  const canvasRef = useRef(null);
  const circlesRef = useRef([]);
  const rafRef = useRef(null);
  const roRef = useRef(null);

  // Resize the internal buffer to EXACTLY match the CSS box (× dpr)
  function syncSizeToCSS(canvas) {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    const cssW = Math.max(1, Math.round(rect.width));
    const cssH = Math.max(1, Math.round(rect.height || 320)); // ensure non-zero height

    const pixelW = Math.max(1, Math.round(cssW * dpr));
    const pixelH = Math.max(1, Math.round(cssH * dpr));

    if (canvas.width !== pixelW || canvas.height !== pixelH) {
      canvas.width = pixelW;
      canvas.height = pixelH;
    }

    // enforce CSS box so there’s no stretch
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    // We draw in CSS pixel coordinates; scale once so 1 unit = 1 CSS px
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Recreate circles for the new CSS size
    circlesRef.current = Array.from({ length: count }, () => createCircle(cssW, cssH));
  }

  function drawFrame() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // Keep the buffer synced to CSS size (handles responsive/layout changes)
    syncSizeToCSS(canvas);

    // Clear in CSS pixel space
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Draw & update circles (all positions/velocities in CSS px)
    for (const c of circlesRef.current) {
      ctx.beginPath();
      ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
      ctx.fillStyle = c.color;
      ctx.fill();

      c.x += c.dx;
      c.y += c.dy;

      // Respawn once well off-screen
      if (c.x < -c.r * 1.2 || c.y < -c.r * 1.2) {
        const w = rect.width;
        const h = rect.height || 320;
        const r = rand(18, 40);
        const speed = rand(0.4, 1.0);
        const angle = rand(Math.PI, Math.PI * 1.45);
        const s = Math.random();
        if (s < 0.33) { c.x = rand(w * 1.02, w * 1.18); c.y = rand(-h * 0.2, h * 1.18); }
        else if (s < 0.66) { c.x = rand(-w * 0.1, w * 1.14); c.y = rand(h * 1.03, h * 1.19); }
        else { c.x = rand(w * 1.06, w * 1.22); c.y = rand(h * 1.09, h * 1.27); }
        c.r = r;
        c.color = COLORS[Math.floor(Math.random() * COLORS.length)];
        c.dx = -Math.abs(Math.cos(angle) * speed);
        c.dy = -Math.abs(Math.sin(angle) * speed);
      }
    }

    rafRef.current = requestAnimationFrame(drawFrame);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Initial sync & start
    syncSizeToCSS(canvas);
    rafRef.current = requestAnimationFrame(drawFrame);

    // Observe CSS size changes precisely (avoids oval artifacts)
    if ("ResizeObserver" in window) {
      roRef.current = new ResizeObserver(() => syncSizeToCSS(canvas));
      roRef.current.observe(canvas);
    } else {
      const onResize = () => syncSizeToCSS(canvas);
      window.addEventListener("resize", onResize);
      roRef.current = { disconnect: () => window.removeEventListener("resize", onResize) };
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      roRef.current?.disconnect?.();
    };
  }, [count]);

  // Allow for ref forwarding if needed
  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      tabIndex={-1}
      style={style}
    />
  );
}
